import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';

import { ApplicationConfigService } from 'services/application-config.service';
import { createRequestOption } from 'app/shared/util/request-util';
import { TransportIncident } from '../models/transport-incident.model';
import { ApiProviderService } from './api-provider.service';
import { API_ENDPOINTS } from '../models/enums/endpoints.enum';
import { LastEstimate } from '../models/estimate-result-bundle.model';

export type EntityResponseType = HttpResponse<TransportIncident>;
export type EntityArrayResponseType = HttpResponse<TransportIncident[]>;

@Injectable({ providedIn: 'root' })
export class TransportIncidentService {
  protected resourceUrl: string = API_ENDPOINTS.TRANSPORT_INCIDENTS;
  protected transportIncidentUrl = API_ENDPOINTS.TRANSPORT_INCIDENTS_ID;
  protected transportIncidentTransportOrderUrl = API_ENDPOINTS.TRANSPORT_INCIDENTS_TRANSPORT_ORDER_ID;
  protected declareTransportIncidentUrl: string = API_ENDPOINTS.TRANSPORT_INCIDENTS_TRANSPORTER_DECLARE;
  protected estimateTransportIncidentUrl: string = API_ENDPOINTS.TRANSPORT_INCIDENTS_ESTIMATE;

  constructor(protected apiProvider: ApiProviderService, protected applicationConfigService: ApplicationConfigService) {}

  public declare(transportIncident: TransportIncident): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(transportIncident);
    return this.apiProvider.post<TransportIncident>(this.declareTransportIncidentUrl, copy).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  public estimate(transportIncident: TransportIncident): Observable<LastEstimate> {
    return this.apiProvider.post<LastEstimate>(this.estimateTransportIncidentUrl, transportIncident).pipe(map((res: HttpResponse<LastEstimate>) => res.body ?? new LastEstimate()));
  }

  create(transportIncident: TransportIncident): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(transportIncident);
    return this.apiProvider.post<TransportIncident>(this.resourceUrl, copy).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(transportIncident: TransportIncident): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(transportIncident);
    return this.apiProvider
      .put<TransportIncident>(this.transportIncidentUrl(transportIncident.id), copy)
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  partialUpdate(transportIncident: TransportIncident): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(transportIncident);
    return this.apiProvider
      .patch<TransportIncident>(this.transportIncidentUrl(transportIncident.id), copy)
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  find(id: number): Observable<EntityResponseType> {
    return this.apiProvider.get<TransportIncident>(this.transportIncidentUrl(id), { observe: 'response' }).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.apiProvider
      .get<TransportIncident[]>(this.resourceUrl, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  findAllForTransportOrder(transportOrderId: number): Observable<EntityArrayResponseType> {
    return this.apiProvider
      .publicGet<TransportIncident[]>(this.transportIncidentTransportOrderUrl(transportOrderId), { observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.apiProvider.delete(this.transportIncidentUrl(id));
  }

  protected convertDateFromClient(transportIncident: TransportIncident): TransportIncident {
    return Object.assign({}, transportIncident, {
      date: transportIncident.date?.isValid() ? transportIncident.date.toJSON() : undefined,
    });
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.date = res.body.date ? dayjs(res.body.date) : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((transportIncident: TransportIncident) => {
        transportIncident.date = transportIncident.date ? dayjs(transportIncident.date) : undefined;
      });
    }
    return res;
  }
}
